import { BaseService } from '../../../../../core/services/base.service';
import {
  CandidateSummaryPayload,
  CandidateSummaryStatusResponse,
  CandidateSummarySubmitResponse,
  CandidateSummaryView,
} from '../models/candidate-summary.model';
import { subscribeCandidateSummaryJobEvents } from './candidate-summary-sse';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

export class CandidateSummaryService {
  public static GetSummary(
    applicationId: string,
    CallbackCatch?: (error: Error) => void,
    spinner = false
  ): Promise<CandidateSummaryView | undefined> {
    return BaseService.Get(
      `/job-applications/${applicationId}/candidate-summary`,
      CallbackCatch,
      spinner
    );
  }

  public static SubmitAsync(
    applicationId: string,
    CallbackCatch?: (error: Error) => void,
    spinner = false
  ): Promise<CandidateSummarySubmitResponse> {
    return BaseService.Post(
      `/job-applications/${applicationId}/candidate-summary/async`,
      {},
      CallbackCatch,
      spinner
    );
  }

  public static GetJobStatus(
    asyncJobId: string,
    CallbackCatch?: (error: Error) => void,
    spinner = false
  ): Promise<CandidateSummaryStatusResponse> {
    return BaseService.Get(
      `/candidate-summary-jobs/${asyncJobId}`,
      CallbackCatch,
      spinner
    );
  }

  public static DownloadPdf(
    applicationId: string,
    CallbackCatch?: (error: Error) => void,
    spinner = false
  ): Promise<Blob> {
    return BaseService.Blob(
      'GET',
      `/job-applications/${applicationId}/candidate-summary/pdf`,
      undefined,
      CallbackCatch,
      spinner
    );
  }

  public static UpdateSummary(
    applicationId: string,
    payload: CandidateSummaryPayload
  ): Promise<CandidateSummaryView> {
    return BaseService.Put(`/job-applications/${applicationId}/candidate-summary`, payload);
  }

  public static async RunGeneration(
    applicationId: string,
    onProgress?: (message: string) => void,
    signal?: AbortSignal
  ): Promise<CandidateSummaryStatusResponse> {
    const submit = await this.SubmitAsync(applicationId, undefined, false);
    onProgress?.(submit.message);

    const asyncJobId = submit.jobId;

    try {
      return await this.waitViaSse(asyncJobId, onProgress, signal);
    } catch (error) {
      if (signal?.aborted) {
        throw error instanceof Error ? error : new Error('Assessment cancelled.');
      }
      return this.waitViaPoll(asyncJobId, onProgress, signal);
    }
  }

  private static waitViaSse(
    asyncJobId: string,
    onProgress?: (message: string) => void,
    signal?: AbortSignal
  ): Promise<CandidateSummaryStatusResponse> {
    return new Promise((resolve, reject) => {
      let settled = false;

      const settleResolve = (response: CandidateSummaryStatusResponse) => {
        if (settled) {
          return;
        }
        settled = true;
        resolve(response);
      };

      const settleReject = (error: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        reject(error);
      };

      const timeoutId = window.setTimeout(() => {
        settleReject(new Error('Assessment generation timed out. Please try again.'));
      }, POLL_TIMEOUT_MS);

      const clear = () => window.clearTimeout(timeoutId);

      void subscribeCandidateSummaryJobEvents(
        asyncJobId,
        {
          onStatus: (event) => onProgress?.(event.message),
          onCompleted: (response) => {
            clear();
            settleResolve(response);
          },
          onFailed: (response) => {
            clear();
            settleReject(
              new Error(response.errorMessage ?? 'Assessment generation failed.')
            );
          },
        },
        signal
      ).catch((error: unknown) => {
        clear();
        if (error instanceof DOMException && error.name === 'AbortError') {
          settleReject(new Error('Assessment cancelled.'));
          return;
        }
        settleReject(
          error instanceof Error ? error : new Error('SSE connection failed.')
        );
      });
    });
  }

  private static waitViaPoll(
    asyncJobId: string,
    onProgress?: (message: string) => void,
    signal?: AbortSignal
  ): Promise<CandidateSummaryStatusResponse> {
    const startedAt = Date.now();

    return new Promise((resolve, reject) => {
      const poll = async () => {
        if (signal?.aborted) {
          reject(new Error('Assessment cancelled.'));
          return;
        }

        try {
          const status = await this.GetJobStatus(asyncJobId, undefined, false);
          onProgress?.(status.message);

          if (status.status === 'COMPLETED') {
            resolve(status);
            return;
          }
          if (status.status === 'FAILED') {
            reject(
              new Error(status.errorMessage ?? 'Assessment generation failed.')
            );
            return;
          }
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            reject(new Error('Assessment generation timed out. Please try again.'));
            return;
          }
          window.setTimeout(poll, POLL_INTERVAL_MS);
        } catch (error) {
          reject(error instanceof Error ? error : new Error('Assessment failed.'));
        }
      };

      void poll();
    });
  }
}
